/**
 * Admin Reconciliation API Service
 * 
 * Provides access to reconciliation monitoring endpoints (PILLAR D).
 */

import { apiClient } from "./apiClient";

export interface ReconciliationSummary {
  timestamp: string;
  stuck_running_jobs: number;
  escalated_jobs: number;
  stuck_outbox: number;
  stuck_intents: number;
  total_stuck_items: number;
}

export interface StuckJob {
  job_id: string;
  job_type: string;
  status: string;
  user_id: string;
  symbol: string;
  reconcile_attempts: number;
  last_reconcile_at: string | null;
  next_reconcile_at: string | null;
  started_at: string | null;
  created_at: string;
  last_error: string | null;
}

export interface StuckOutbox {
  id: string;
  job_id: string;
  status: string;
  publish_attempts: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  reconcile_attempts: number;
  last_reconcile_at: string | null;
  next_reconcile_at: string | null;
}

export interface StuckIntent {
  id: string;
  account_id: string;
  order_link_id: string | null;
  order_id: string | null;
  symbol: string;
  user_id: string | null;
  state: string;
  reconcile_attempts: number;
  last_reconcile_at: string | null;
  next_reconcile_at: string | null;
  updated_at: string;
  created_at: string;
}

export interface StuckItemsResponse {
  jobs: {
    count: number;
    items: StuckJob[];
  };
  outbox: {
    count: number;
    items: StuckOutbox[];
  };
  intents: {
    count: number;
    items: StuckIntent[];
  };
}

/**
 * Get reconciliation summary (counts only)
 */
export async function getReconciliationSummary(): Promise<ReconciliationSummary> {
  const response = await apiClient.get<ReconciliationSummary>(
    "/admin/reconciliation/summary",
  );
  return response.data;
}

/**
 * Get stuck items requiring reconciliation
 */
export async function getStuckItems(params?: {
  item_type?: string;
  status_filter?: string;
  symbol?: string;
  user_id?: string;
  limit?: number;
}): Promise<StuckItemsResponse> {
  const response = await apiClient.get<StuckItemsResponse>(
    "/admin/reconciliation/stuck-items",
    { params },
  );
  return response.data;
}

